/**
 * TitleScene.ts — Main menu for Gallowcrest.
 *
 * Displays the game title in ASCII art with menu options:
 * - New Game -> CharCreateScene
 * - Continue -> loads most recent save, jumps to StoryScene
 * - Load Game -> LoadGameScene
 * - Settings -> SettingsScene
 * - Quit
 *
 * Migrated to FrameRenderer + PanelWriter for framed UI layout.
 * Snow particles active. TerminalFrame minimized for clean title look.
 */

import type { GameEngine, Scene } from '@core/engine/GameEngine';
import type { MenuOption } from '@ui/MenuSystem';
import { CharCreateScene } from './CharCreateScene';
import { LoadGameScene } from './LoadGameScene';
import { SettingsScene } from './SettingsScene';
import { AchievementScene } from './AchievementScene';
import { HelpScene } from './HelpScene';
import { StoryScene } from './StoryScene';

export class TitleScene implements Scene {
  name = 'TitleScene';

  async enter(engine: GameEngine): Promise<void> {
    const ctx = engine.getContext();
    const { terminal, effects, menu, statusBar, saveSystem, storyEngine, settings, visualLayer, frameRenderer } = ctx;

    // Hide status bar on title screen
    statusBar.hide();

    // Set snow particles for title
    visualLayer.setLocationParticles('gallowcrest_citadel');

    await this.renderTitle(engine);

    // Main menu loop
    while (true) {
      const mostRecent = saveSystem.getMostRecentSave();
      const hasSaves = mostRecent !== null;

      const options: MenuOption[] = [
        { label: 'New Game', color: 'green' },
        hasSaves
          ? { label: 'Continue', color: 'amber', suffix: `(${mostRecent.metadata.characterName} Lv.${mostRecent.metadata.level})`, suffixColor: 'frostSilver' }
          : { label: 'Continue', color: 'charcoal', disabled: true, suffix: '(no saves)' },
        { label: 'Load Game', color: 'amber', disabled: !hasSaves },
        { label: 'Achievements', color: 'amber' },
        { label: 'Help', color: 'amber' },
        { label: 'Settings', color: 'amber' },
      ];

      // Redraw title before showing menu to ensure clean state
      this.renderTitleSync(engine);

      // Position menu below the title art
      const cursorRow = terminal.getCursorRow();
      const selection = await menu.show(options, {
        showExit: true,
        exitLabel: 'Quit',
        startRow: cursorRow + 1,
        onBeforeRender: () => {
          this.renderTitleSync(engine);
        },
      });

      console.log(`[TitleScene] Menu selection: ${selection}, options.length: ${options.length}`);

      switch (selection) {
        case 0: // New Game
          await engine.pushScene(new CharCreateScene());
          return;

        case 1: { // Continue
          if (!mostRecent) break;
          const state = saveSystem.load(mostRecent.slot);
          if (state) {
            state.setEventSystem(ctx.events);
            ctx.gameState = state;
            storyEngine.setState(state);

            // Show loading in frame
            ctx.sprites.hide();
            const loadLayout = frameRenderer.drawFrame({
              title: 'LOADING...',
              borderColor: 'frostBlue',
            });
            const loadWriter = frameRenderer.createWriter(loadLayout.content, effects);
            const offset = loadWriter.calculateVerticalOffset(3);
            loadWriter.skip(offset);

            try {
              await effects.withLoading('  Loading...', storyEngine.initialize());
              await engine.replaceScene(new StoryScene());
              return;
            } catch (err) {
              console.error('Failed to load story data:', err);
              loadWriter.blankLine();
              loadWriter.writeLine('  Failed to load story data.', 'red');
              ctx.gameState = null;
              await loadWriter.pressAnyKey();
              await this.renderTitle(engine);
              break;
            }
          }
          ctx.sprites.hide();
          const errLayout = frameRenderer.drawFrame({
            title: 'ERROR',
            borderColor: 'red',
          });
          const errWriter = frameRenderer.createWriter(errLayout.content, effects);
          errWriter.blankLine();
          errWriter.writeLine('  Failed to load save.', 'red');
          await errWriter.pressAnyKey();
          await this.renderTitle(engine);
          break;
        }

        case 2: // Load Game
          await engine.pushScene(new LoadGameScene());
          return;

        case 3: // Achievements
          await engine.pushScene(new AchievementScene());
          return;

        case 4: // Help
          await engine.pushScene(new HelpScene());
          return;

        case 5: // Settings
          await engine.pushScene(new SettingsScene());
          return;

        case -1: { // Quit
          ctx.sprites.hide();
          const quitLayout = frameRenderer.drawFrame({
            title: 'FAREWELL',
            borderColor: 'frostBlue',
          });
          const quitWriter = frameRenderer.createWriter(quitLayout.content, effects);
          const offset = quitWriter.calculateVerticalOffset(6);
          quitWriter.skip(offset);
          await quitWriter.typewriterLine('  The frost claims another soul...', 'frostSilver', settings.getTypewriterSpeed());
          quitWriter.blankLine();
          quitWriter.writeLine('  Thanks for playing Gallowcrest.', 'white');
          quitWriter.writeLine('  A ChoiceRealms Adventure.', 'frostSilver');
          engine.stop();
          return;
        }
      }
    }
  }

  async exit(engine: GameEngine): Promise<void> {
    engine.getContext().sprites.hide();
  }

  private async renderTitle(engine: GameEngine): Promise<void> {
    const { terminal, art, effects, viewport } = engine.getContext();

    terminal.clear();

    // Show viewport warning if critically small
    viewport.showViewportWarning();
    terminal.blankLine();

    // Render title art with viewport-aware sizing
    const vp = viewport.getViewportClass();
    const artHeight = viewport.getArtHeight();

    if (vp === 'tiny' || vp === 'small') {
      // Skip frostcrown art on small viewports, show compact title only
      art.renderCentered('title', 'brightWhite', artHeight);
      art.renderCentered('subtitle', 'frostSilver');
      art.renderCentered('tagline', 'khaki');
    } else {
      // Full experience: frostcrown + title
      art.renderCentered('frostcrown', 'frostBlue', artHeight);
      terminal.blankLine();
      art.renderCentered('title', 'brightWhite');
      art.renderCentered('subtitle', 'frostSilver');
      art.renderCentered('tagline', 'khaki');
    }
    terminal.blankLine();

    // Brief dramatic pause
    await effects.delay(500);
  }

  /** Synchronous title redraw for onBeforeRender callback. */
  private renderTitleSync(engine: GameEngine): void {
    const { terminal, art, viewport } = engine.getContext();

    terminal.clear();

    // Show viewport warning if critically small
    viewport.showViewportWarning();
    terminal.blankLine();

    // Render title art with viewport-aware sizing
    const vp = viewport.getViewportClass();
    const artHeight = viewport.getArtHeight();

    if (vp === 'tiny' || vp === 'small') {
      // Skip frostcrown art on small viewports, show compact title only
      art.renderCentered('title', 'brightWhite', artHeight);
      art.renderCentered('subtitle', 'frostSilver');
      art.renderCentered('tagline', 'khaki');
    } else {
      // Full experience: frostcrown + title
      art.renderCentered('frostcrown', 'frostBlue', artHeight);
      terminal.blankLine();
      art.renderCentered('title', 'brightWhite');
      art.renderCentered('subtitle', 'frostSilver');
      art.renderCentered('tagline', 'khaki');
    }
    terminal.blankLine();
  }
}
